/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../reducers';
import { ScopeQuestionOptionalValue } from '../types/surveyScope';
import UniversalTracker from '../model/UniversalTracker';
import {
  getQuestionName,
  getQuestionShortPrefix
} from '../utils/universalTracker';
import { UniversalTrackerBlueprintMin } from '../types/universalTracker';
import G17Client from '../services/G17Client';

interface LoadPayload {
  surveyId: string | undefined,
  templateId: string | undefined,
}

const blueprintQuestionsSlice = createSlice({
  name: 'blueprintQuestions',
  initialState: { loaded: false, errored: false, errorMessage: '', code: '', data: [] },
  reducers: {
    loadQuestions: (state, action: PayloadAction<LoadPayload>) => {
      return { ...state, errored: false, errorMessage: '',  ...action.payload };
    },
    loadedQuestions: (state, action) => {
      return { ...state, loaded: true, data: action.payload, }
    },
    error: (state, action: PayloadAction<Error>) => {
      return { ...state, errored: true, errorMessage: action.payload.message };
    }
  }
})

const { loadQuestions, loadedQuestions, error } = blueprintQuestionsSlice.actions

export default blueprintQuestionsSlice.reducer

const selectBlueprintQuestions = (state: RootState) => state.blueprintQuestions.data;

export const mapScopeQuestion = (blueprintQuestions: UniversalTrackerBlueprintMin[]) => {
  return blueprintQuestions.map((u) => {
    const universalTracker = new UniversalTracker(u);
    return {
      universalTracker,
      name: getQuestionName(universalTracker),
      shortPrefix: getQuestionShortPrefix(universalTracker),
      frameworkCode: universalTracker.getFrameworkCode(),
      valueType: universalTracker.getValueType(),
      valueValidation: universalTracker.getValueValidation(),
    } as ScopeQuestionOptionalValue
  });
}

export const getAsScopeQuestionNoValue = createSelector(
  [selectBlueprintQuestions],
  mapScopeQuestion
);

export const loadBlueprintQuestions = (surveyId: string): any => async (dispatch: any, getState: () => any) => {

  const state = getState().blueprintQuestions;
  if (surveyId === state.surveyId && state.loaded) {
    return;
  }

  dispatch(loadQuestions({ templateId: undefined, surveyId }));
  G17Client.getBlueprintQuestionsWithCustomMetrics(surveyId).then(
    (utrs: UniversalTrackerBlueprintMin[]) => {
      dispatch(loadedQuestions(utrs))
    },
    (err: Error) => {
      dispatch(error(err));
    }
  )
}

export const loadTemplateBlueprintQuestions = (templateId: string): any => async (dispatch: any, getState: () => any) => {

  const state = getState().blueprintQuestions;
  if (templateId === state.templateId && state.loaded) {
    return;
  }

  dispatch(loadQuestions({ surveyId: undefined, templateId }));

  G17Client.getTemplateBlueprintQuestions(templateId).then(
    (utrs: UniversalTrackerBlueprintMin[]) => {
      dispatch(loadedQuestions(utrs))
    },
    (err: Error) => {
      dispatch(error(err));
    }
  )
}

